import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const MethodologyPage = () => (
  <main className="min-h-screen bg-slate-50" data-testid="methodology-page">
    <Disclaimer />
    <div className="container-shell py-12 max-w-4xl">
      <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: "'Fraunces', serif" }}>Methodology</h1>
      
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-4">Deterministic Scoring Architecture</h2>
          <p className="text-muted-foreground mb-4">
            Trialeze employs a deterministic scoring engine that calculates procedural metrics using weighted 
            formulas derived from structured litigation inputs. All scores are computed algorithmically, 
            ensuring reproducible and explainable outputs.
          </p>
          <Card className="p-6">
            <h3 className="font-semibold mb-3">Core Scoring Formula</h3>
            <code className="block bg-slate-100 p-4 rounded-lg text-sm font-mono">
              Score = Σ(Input_Weight × Normalized_Value) × Category_Factor
            </code>
            <p className="text-sm text-muted-foreground mt-3">
              Each metric applies specific weights to relevant inputs, normalized to a 0-100 scale.
            </p>
          </Card>
        </section>
        
        <section>
          <h2 className="text-2xl font-semibold mb-4">11 Procedural Metrics</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              ["Procedural Friction", "Case complexity weighted by venue, counsel patterns, and documentation gaps"],
              ["Documentation Strength", "Evidence quality composite from police reports, media, witnesses, completeness"],
              ["Insurance Leverage", "Coverage status, visibility, liability clarity, and damages support"],
              ["Venue Pressure", "Backlog levels, formality, judge environment, jury factors"],
              ["Opposing Counsel Intensity", "Motion patterns, discovery aggressiveness, delay tendencies"],
              ["Judge Structure", "Procedural strictness indicators from court records"],
              ["Jury Volatility", "Presentation risk based on damages clarity and incident complexity"],
              ["Financial Recoverability", "Asset and coverage indicators affecting collection"],
              ["Medical Documentation", "Treatment chronology completeness and intensity"],
              ["Case Economics", "Composite pressure from multiple financial factors"],
              ["Data Confidence", "Input completeness and consistency measurement"]
            ].map(([title, desc]) => (
              <div key={title} className="p-4 border rounded-xl">
                <h4 className="font-medium mb-1">{title}</h4>
                <p className="text-sm text-muted-foreground">{desc}</p>
              </div>
            ))}
          </div>
        </section>
        
        <section>
          <h2 className="text-2xl font-semibold mb-4">Judge & Attorney Pattern Analysis</h2>
          <p className="text-muted-foreground mb-4">
            Procedural pattern data is aggregated from publicly available court records, docket filings, 
            and published opinions. Analysis focuses on observable procedural tendencies, not character 
            or competence assessments.
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="p-4">
              <h4 className="font-medium mb-2">Data Sources</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Court docket records</li>
                <li>• Published opinions</li>
                <li>• Bar association records</li>
                <li>• Public case filings</li>
              </ul>
            </Card>
            <Card className="p-4">
              <h4 className="font-medium mb-2">Judge Metrics</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Motion disposition patterns</li>
                <li>• Scheduling tendencies</li>
                <li>• Settlement approaches</li>
                <li>• Procedural strictness</li>
              </ul>
            </Card>
            <Card className="p-4">
              <h4 className="font-medium mb-2">Attorney Metrics</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Case volume distribution</li>
                <li>• Motion practice frequency</li>
                <li>• Duration patterns</li>
                <li>• Venue preferences</li>
              </ul>
            </Card>
          </div>
        </section>
        
        <section>
          <h2 className="text-2xl font-semibold mb-4">Report Narrative Synthesis</h2>
          <p className="text-muted-foreground mb-4">
            Analytical narratives are synthesized by processing structured score outputs through 
            templated language rules. The narrative layer interprets computed scores based on 
            threshold conditions and pattern matching, but does not influence or modify the 
            underlying calculations.
          </p>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              All scoring is deterministic and reproducible. Narrative outputs provide contextual 
              interpretation of calculated metrics and should be reviewed by qualified legal counsel.
            </AlertDescription>
          </Alert>
        </section>
        
        <section>
          <h2 className="text-2xl font-semibold mb-4">Transparency & Reproducibility</h2>
          <p className="text-muted-foreground">
            Every Trialeze output includes data confidence scores reflecting input completeness. 
            The same inputs will always produce identical scores, enabling verification and 
            audit trails. This deterministic approach ensures defensible analytics suitable 
            for professional legal work product.
          </p>
        </section>
      </div>
    </div>
    <Footer />
  </main>
);


export default MethodologyPage;
